import React from 'react';
import { Link } from 'react-router-dom';
import {
  BrainCircuit,
  Database,
  Globe,
  Award,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';

export const Intelligence: React.FC = () => {
  const howWeWorkItems = [
    {
      title: 'Strong accounting foundations + digital systems + AI',
      desc: "We don't replace your accounting. We enhance it with intelligence.",
      icon: BrainCircuit,
    },
    {
      title: 'Platform agnostic approach',
      desc: 'Works seamlessly with Tally, Zoho Books, and QuickBooks. No migration required.',
      icon: Database,
    },
    {
      title: 'Active across three regions',
      desc: 'India, Middle East, and Southeast Asia — we understand your markets, tax codes, and cross-border delivery.',
      icon: Globe,
    },
    {
      title: 'Experienced founding team',
      desc: '2nd-generation Chartered Accountant + Digital Operations specialists from top startup ecosystems.',
      icon: Award,
    },
    {
      title: 'Startups and MSME-focused intelligence',
      desc: 'We build the intelligence layer that was previously only accessible to large Fortune-500 enterprises.',
      icon: TrendingUp,
    },
  ];

  const whatYouGetItems = [
    'Autonomous bookkeeping + WhatsApp & voice note ingestion',
    'Native TallyPrime, Zoho Books & QuickBooks synchronization',
    '1-click board MIS & PowerPoint (.pptx) presentation decks',
    'Continuous MSME 43B(h) & Form 3CD tax audit sentinel',
    'Automated 3-way aggregator, EDC swipe & bank reconciliation',
    'P&L, Balance Sheet, Cash Flow dashboards with drill-downs',
    'Profitability by product line & customer ranking',
    'Budget vs actual tracking — every month',
    '60–90 day rolling cash flow forecasting',
    'Automated Statement of Accounts (SOA) with WhatsApp reminders',
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Header */}
      <section
        style={{
          paddingTop: '64px',
          paddingBottom: '80px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'radial-gradient(ellipse 70% 50% at 50% 0%, var(--brand-red-glow-subtle), transparent)',
        }}
      >
        <div className="container-max" style={{ textAlign: 'center', maxWidth: '840px' }}>
          <span className="badge-pill badge-red" style={{ marginBottom: '16px' }}>
            OUR APPROACH
          </span>
          <h1
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.8rem)',
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              marginBottom: '20px',
            }}
          >
            From Accounting to Intelligence
          </h1>
          <p
            style={{
              fontSize: '1.2rem',
              color: 'var(--text-primary)',
              lineHeight: '1.65',
              marginBottom: '16px',
            }}
          >
            Forty2 moves Startups and MSMEs from traditional accounting to a fully intelligent financial operating system. We combine strong foundations, digital systems, and AI — working together to transform how you understand and run your business.
          </p>
          <p
            className="font-serif"
            style={{
              fontSize: '1.45rem',
              fontStyle: 'italic',
              color: 'var(--brand-red-light)',
            }}
          >
            Finance should not just record the past. It should help decide the future.
          </p>
        </div>
      </section>

      {/* How We Work Section */}
      <section style={{ padding: '96px 0' }}>
        <div className="container-max">
          <div style={{ maxWidth: '680px', marginBottom: '56px' }}>
            <span className="badge-pill badge-neutral font-mono" style={{ marginBottom: '12px' }}>
              METHODOLOGY
            </span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '14px' }}>
              How We Work
            </h2>
            <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)' }}>
              We build the intelligence layer that only large enterprises have had — and make it accessible to growth-stage Startups and MSMEs.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px',
            }}
          >
            {howWeWorkItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="glass-card-interactive"
                  style={{
                    padding: '32px 28px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(229, 57, 53, 0.1)',
                        border: '1px solid rgba(229, 57, 53, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px',
                      }}
                    >
                      <Icon size={24} color="var(--brand-red)" />
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
                      {item.title}
                    </h3>
                    <p style={{ fontSize: '0.94rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section
        style={{
          padding: '96px 0',
          backgroundColor: 'var(--bg-surface)',
          borderTop: '1px solid var(--border-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div className="container-max">
          <div style={{ maxWidth: '640px', marginBottom: '48px' }}>
            <span className="badge-pill badge-red" style={{ marginBottom: '12px' }}>
              DELIVERABLES
            </span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '12px' }}>
              What You Get
            </h2>
            <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)' }}>
              Two core pillars that work together to transform your financial operations into a competitive advantage.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '16px',
            }}
          >
            {whatYouGetItems.map((item, idx) => (
              <div
                key={idx}
                className="glass-panel"
                style={{
                  padding: '20px 22px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                }}
              >
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 230, 118, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CheckCircle2 size={16} color="var(--signal-green)" />
                </div>
                <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Maxim Banner */}
      <section style={{ padding: '80px 0', textAlign: 'center' }}>
        <div className="container-max" style={{ maxWidth: '720px' }}>
          <h2
            className="font-serif"
            style={{
              fontSize: 'clamp(2rem, 3.8vw, 2.85rem)',
              fontStyle: 'italic',
              color: 'var(--text-primary)',
              marginBottom: '20px',
            }}
          >
            Clarity is more valuable than data.
          </h2>
          <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', marginBottom: '32px' }}>
            Discover how Forty2 combines software automation and Chartered Accountant expertise to give you the clarity you need.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <Link to="/contact" className="btn-primary" style={{ padding: '14px 32px' }}>
              <span>Consult with Our Team</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
